import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import type { ReactNode } from "react";

import { getProfile, login as loginRequest } from "@/features/auth/api/authApi";
import { normalizeAuthUser } from "@/features/auth/authUser";
import {
    hasAllPermissions,
    hasAnyPermission,
    hasPermission,
    type PermissionCode,
} from "@/features/auth/permissions";
import type { AuthUser } from "@/features/auth/types";
import { extractApiError } from "@/lib/api-error";
import { tokenStorage } from "@/lib/http";
import { authLogger } from "@/lib/logger";

import {
    AUTH_FORCE_LOGOUT_EVENT,
    INACTIVITY_TIMEOUT_MS,
    LAST_ACTIVITY_AT_STORAGE_KEY,
    clearSessionStorage,
    dispatchForcedLogout,
    readLastActivityAt,
    readStoredUser,
    redirectToLogin,
    storeUser,
    touchSessionActivity,
    type ForcedLogoutDetail,
} from "./session";

type AuthContextValue = {
    isAuthenticated: boolean;
    isLoading: boolean;
    user: AuthUser | null;
    login: (email: string, password: string) => Promise<void>;
    logout: () => Promise<void>;
    refreshProfile: () => Promise<void>;
    hasPermission: (permission: PermissionCode) => boolean;
    hasAnyPermission: (permissions: readonly PermissionCode[]) => boolean;
    hasAllPermissions: (permissions: readonly PermissionCode[]) => boolean;
};

const AuthContext = createContext<AuthContextValue | null>(null);

export const AuthProvider = ({ children }: { children: ReactNode }) => {
    const [isAuthenticated, setIsAuthenticated] = useState<boolean>(
        () => tokenStorage.hasToken() && readStoredUser() !== null
    );
    const [isLoading, setIsLoading] = useState(false);
    const [user, setUser] = useState<AuthUser | null>(() => readStoredUser());
    const inactivityTimeoutRef = useRef<number | null>(null);
    const lastInteractionRef = useRef(0);

    const applyLoggedOutState = useCallback(() => {
        clearSessionStorage();
        setUser(null);
        setIsAuthenticated(false);
    }, []);

    const refreshProfile = useCallback(async () => {
        if (!tokenStorage.hasToken()) {
            return;
        }

        const response = await getProfile();
        const normalizedUser = normalizeAuthUser(response.data);

        if (!response.status || !normalizedUser) {
            throw new Error(response.message || "Unable to refresh session profile");
        }

        storeUser(normalizedUser);
        setUser(normalizedUser);
        setIsAuthenticated(true);
    }, []);

    const login = useCallback(async (email: string, password: string) => {
        setIsLoading(true);
        try {
            const response = await loginRequest({ email, password });

            if (!response.status) {
                throw new Error(response.message || "Unable to sign in");
            }

            // Extract tokens from top level and user data from nested data
            const { accessToken, refreshToken, data: userData } = response;
            const normalizedUser = normalizeAuthUser(userData);
            if (!accessToken || !normalizedUser) {
                throw new Error("Missing session details from login response");
            }

            tokenStorage.setTokens(accessToken, refreshToken);
            storeUser(normalizedUser);
            touchSessionActivity();
            setUser(normalizedUser);
            setIsAuthenticated(true);
        } catch (error) {
            setUser(null);
            setIsAuthenticated(false);
            throw new Error(extractApiError(error));
        } finally {
            setIsLoading(false);
        }
    }, []);

    const logout = useCallback(async () => {
        setIsLoading(true);
        try {
            applyLoggedOutState();
            // TODO: call logout endpoint when available
        } finally {
            setIsLoading(false);
        }
    }, [applyLoggedOutState]);

    useEffect(() => {
        const hasToken = tokenStorage.hasToken();
        const storedUser = readStoredUser();

        if (hasToken && storedUser) {
            setUser(storedUser);
            setIsAuthenticated(true);
            return;
        }

        applyLoggedOutState();
    }, [applyLoggedOutState]);

    useEffect(() => {
        if (!tokenStorage.hasToken()) {
            return;
        }

        let active = true;

        const syncProfile = async () => {
            setIsLoading(true);
            try {
                const response = await getProfile();
                const normalizedUser = normalizeAuthUser(response.data);

                if (!response.status || !normalizedUser) {
                    throw new Error(response.message || "Unable to refresh session profile");
                }

                if (!active) {
                    return;
                }

                storeUser(normalizedUser);
                setUser(normalizedUser);
                setIsAuthenticated(true);
            } catch (error) {
                if (!active) {
                    return;
                }

                authLogger.warn(`Failed to refresh session profile: ${extractApiError(error)}`);
                applyLoggedOutState();
            } finally {
                if (active) {
                    setIsLoading(false);
                }
            }
        };

        syncProfile();

        return () => {
            active = false;
        };
    }, [applyLoggedOutState]);

    useEffect(() => {
        const handleForcedLogout = (event: Event) => {
            const reason = (event as CustomEvent<ForcedLogoutDetail>).detail?.reason ?? "unauthorized";
            authLogger.warn(`Session ended (${reason}). Redirecting to login.`);
            applyLoggedOutState();
            redirectToLogin();
        };

        window.addEventListener(AUTH_FORCE_LOGOUT_EVENT, handleForcedLogout as EventListener);

        return () => {
            window.removeEventListener(AUTH_FORCE_LOGOUT_EVENT, handleForcedLogout as EventListener);
        };
    }, [applyLoggedOutState]);

    useEffect(() => {
        if (!isAuthenticated) {
            if (inactivityTimeoutRef.current) {
                window.clearTimeout(inactivityTimeoutRef.current);
                inactivityTimeoutRef.current = null;
            }
            return;
        }

        const clearInactivityTimeout = () => {
            if (!inactivityTimeoutRef.current) {
                return;
            }

            window.clearTimeout(inactivityTimeoutRef.current);
            inactivityTimeoutRef.current = null;
        };

        const scheduleForcedLogout = (expiresAt: number) => {
            clearInactivityTimeout();

            const remainingMs = expiresAt - Date.now();
            if (remainingMs <= 0) {
                dispatchForcedLogout("inactivity");
                return;
            }

            inactivityTimeoutRef.current = window.setTimeout(() => {
                dispatchForcedLogout("inactivity");
            }, remainingMs);
        };

        const markActivity = (persist = true) => {
            const now = Date.now();
            const elapsedSinceLastInteraction = now - lastInteractionRef.current;
            const shouldPersist = persist && elapsedSinceLastInteraction >= 1000;

            if (shouldPersist) {
                touchSessionActivity(now);
                lastInteractionRef.current = now;
            }

            scheduleForcedLogout(now + INACTIVITY_TIMEOUT_MS);
        };

        const storedLastActivity = readLastActivityAt();
        const initialActivity = storedLastActivity ?? Date.now();
        if (!storedLastActivity) {
            touchSessionActivity(initialActivity);
        }
        lastInteractionRef.current = initialActivity;
        scheduleForcedLogout(initialActivity + INACTIVITY_TIMEOUT_MS);

        const activityEvents: Array<keyof WindowEventMap> = ["pointerdown", "keydown", "scroll", "touchstart"];
        const handleUserActivity = () => markActivity(true);
        const handleVisibilityChange = () => {
            if (document.visibilityState === "visible") {
                markActivity(true);
            }
        };
        const handleStorageChange = (event: StorageEvent) => {
            if (event.key !== LAST_ACTIVITY_AT_STORAGE_KEY) {
                return;
            }

            const nextActivity = event.newValue ? Number(event.newValue) : Number.NaN;
            if (Number.isFinite(nextActivity)) {
                scheduleForcedLogout(nextActivity + INACTIVITY_TIMEOUT_MS);
            }
        };

        activityEvents.forEach((eventName) => {
            window.addEventListener(eventName, handleUserActivity, { passive: true });
        });
        document.addEventListener("visibilitychange", handleVisibilityChange);
        window.addEventListener("storage", handleStorageChange);

        return () => {
            activityEvents.forEach((eventName) => {
                window.removeEventListener(eventName, handleUserActivity);
            });
            document.removeEventListener("visibilitychange", handleVisibilityChange);
            window.removeEventListener("storage", handleStorageChange);
            clearInactivityTimeout();
        };
    }, [isAuthenticated]);

    const value = useMemo<AuthContextValue>(
        () => ({
            isAuthenticated,
            isLoading,
            user,
            login,
            logout,
            refreshProfile,
            hasPermission: (permission) => hasPermission(user?.effectivePermissions, permission),
            hasAnyPermission: (permissions) =>
                hasAnyPermission(user?.effectivePermissions, permissions),
            hasAllPermissions: (permissions) =>
                hasAllPermissions(user?.effectivePermissions, permissions),
        }),
        [isAuthenticated, isLoading, login, logout, refreshProfile, user]
    );

    return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = () => {
    const ctx = useContext(AuthContext);
    if (!ctx) {
        throw new Error("useAuth must be used within an AuthProvider");
    }
    return ctx;
};
